import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import classnames from 'classnames';
import classNames from 'classnames';
import { getGlobalStyleSheets } from '../../../global-styles';
import { booleanAttributeConverter } from '../../helpers';
import { AtlSelectMultiProps, AtlSelectOption } from './props';
import accessibilityHelper from '../../../accessibilityHelper';
import {
   Sizes,
   SizesValuesMap,
   enumAttributeConverter,
} from '../../enum-types';

import * as TagNames from './select.tagnames';
import { AtlSelectChangeEvent } from './select.events';

@customElement(TagNames.SelectMulti)
export class AtlSelectMulti extends LitElement implements AtlSelectMultiProps {
   declare internalId: string;
   declare name: string;
   declare size?: Sizes;
   declare placeholder?: string;
   declare selectClassName?: string;
   declare selectValue: Array<AtlSelectOption> | undefined;
   declare options: Array<AtlSelectOption>;
   declare selectInactive?: boolean;
   declare protected toggled: boolean;

   static properties = {
      internalId: { type: String },
      selectClassName: { type: String },
      name: { type: String },
      size: {
         type: String,
         convertor: enumAttributeConverter(Sizes, Sizes.Medium),
      },
      placeholder: { type: String },
      selectValue: { type: Array<AtlSelectOption[]>(), state: true },
      options: { type: Array<AtlSelectOption[]>(), state: true },
      selectInactive: { type: String, converter: booleanAttributeConverter() },
      toggled: { type: Boolean, state: true },
   };

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         .dropdown-item:hover {
            cursor: pointer;
         }
      `,
   ];

   constructor() {
      super();
      this.selectClassName = '';
      this.name = '';
   }

   renderOptionsToShow() {
      const optionsToRender = this.options.filter(
         (o) =>
            (this.selectValue &&
               !this.selectValue.some((sv) => sv.value === o.value)) ??
            true
      );

      if (optionsToRender.length > 0) {
         return html`
            ${optionsToRender
               .filter(
                  (o) =>
                     (this.selectValue &&
                        !this.selectValue.some((sv) => sv.value === o.value)) ??
                     true
               )
               .map((o) => this.renderOption(o))}
         `;
      }

      return html`<li
         class=${classnames(
            'dropdown-item p-2 form-select',
            SizesValuesMap[this.size ?? Sizes.Medium]
         )}
         data-type="user-profile-menu-item"
         tabindex="0"
      >
         No more options to choose.
      </li>`;
   }

   renderIcon(option: AtlSelectOption) {
      if (!option.icon) return nothing;

      return html`<i
         tabindex="-1"
         class=${`fa-solid fa-${option.icon} ${option.iconClassName}`}
      ></i>`;
   }

   renderOption(option: AtlSelectOption) {
      return html`
         <li
            id=${`${this.internalId}-${option.value}`}
            class=${classnames(
               'dropdown-item p-2 form-select',
               SizesValuesMap[this.size ?? Sizes.Medium]
            )}
            data-type="user-profile-menu-item"
            tabindex="0"
            @click=${() => this._handleSelection(option.value)}
            @keydown=${(e: KeyboardEvent) =>
               accessibilityHelper.onKeyDownWrapper(e, () =>
                  this._handleItemKeyDown(e, option.value)
               )}
         >
            <div tabindex="0">${this.renderIcon(option)} ${option.label}</div>
         </li>
      `;
   }

   renderSingleMultiInput(option: AtlSelectOption) {
      const clearIcon = html`
         <i
            class="fa-solid fa-times"
            style="font-size: 1rem; cursor: pointer;"
            @click=${() => this.handleClear(option.value)}
            tabindex="0"
         >
         </i>
      `;
      return html`
         <div
            style="background-color: lightgray; width: fit-content; margin: 5px; border-radius: 3px;padding: 0px 10px 0px 10px;"
         >
            ${option.label} ${clearIcon}
         </div>
      `;
   }

   renderInput() {
      if (this.selectValue && this.selectValue.length > 0) {
         return html`
            <div
               id=${this.internalId}
               class=${classnames(
                  'form-control form-select',
                  this.selectClassName,
                  SizesValuesMap[this.size ?? Sizes.Medium]
               )}
               title=${this.name}
               disabled="${true}"
               style="display: flex; flex-wrap: wrap;"
               @click=${() => this._handleOnChange()}
               tabindex="0"
            >
               ${html`${this.selectValue.map((sv) =>
                  this.renderSingleMultiInput(sv)
               )}`}
            </div>
         `;
      }

      return html`
         <div
            class=${classnames(
               'form-control form-select',
               this.selectClassName,
               SizesValuesMap[this.size ?? Sizes.Medium]
            )}
            title=${this.name}
            type="select"
            @click=${() => this._handleOnChange()}
            style="cursor:pointer;"
            tabindex="0"
         >
            ${this.placeholder ?? 'Please select...'}
         </div>
      `;
   }

   render() {
      return html`
         <div class="dropdown">
            <span
               id="atl-drop-down-menu-cont"
               data-bs-toggle="dropdown"
               aria-haspopup="true"
               aria-label="avatar-button-label"
               data-type="user-profile-menu-toggle"
            >
               ${this.renderInput()}
            </span>
            <ul
               class=${classNames(
                  'dropdown-menu py-0 border border-secondary rounded w-100',
                  { show: this.toggled }
               )}
               data-bs-popper="none"
               data-type="user-profile-menu-dropdown"
            >
               ${this.renderOptionsToShow()}
            </ul>
         </div>
      `;
   }

   protected _handleOnChange = () => {
      window.addEventListener('click', (e: MouseEvent) => {
         const target = e.target as HTMLElement;
         if (target?.nodeName !== 'ATLANTIS-SELECT-MULTI') {
            this.toggled = false;
         }

         window.removeEventListener('click', () => {});
      });
      this.toggled = true;
   };

   protected _handleSelection = (value: string) => {
      const selectedValue =
         (this.options && this.options.find((o) => o.value === value)) ??
         undefined;
      if (this.selectValue && selectedValue) {
         this.selectValue = [...this.selectValue, selectedValue];
      } else if (selectedValue) {
         this.selectValue = [selectedValue];
      }
      this.toggled = true;
      this.dispatchEvent(new AtlSelectChangeEvent(this.selectValue));
   };

   protected _handleItemKeyDown = (e: KeyboardEvent, value: string) => {
      if (e.key === 'Enter') {
         this._handleSelection(value);
      }
   };

   protected handleClear = (value: string) => {
      if (this.selectValue) {
         this.selectValue = this.selectValue.filter((sv) => sv.value !== value);
         this.dispatchEvent(new AtlSelectChangeEvent(this.selectValue));
      }
   };
}
